import React, { FC, useCallback, useMemo, useState } from 'react'
import { IColumn } from 'typescript/interfaces/tableUi'
import TableComponent, { TableComponentType } from 'UI/Table'
import { useTableComponentStyle } from 'UI/Table/style'
import { IUploadedCategoriesKeys, IDealDocumentConnectParamsResponse } from 'typescript/interfaces/deals'
import { Link } from 'react-router-dom'
import { getFile } from 'services/storage'
import { createLinkToDownLoad } from 'utils/downloadFile'
import { useQuery } from 'react-query'
import { getClientBankAccounts } from 'services/clients'
import { monthNames } from 'constants/misc'
import { getBankStatementFileName, getRollingMonthWindow } from 'utils/bankStatementHelpers'
import SelectComponent from 'UI/Select'
import { Box, MenuItem, Typography } from '@material-ui/core'
import { convertDateWithTime } from 'utils/formatters'
import { useStyles } from './style'

const TableRelatedDocuments = TableComponent as TableComponentType<IDealDocumentConnectParamsResponse>

interface IDealDocumentProps {
  documents: Array<IDealDocumentConnectParamsResponse>
  clientId?: string | null
  dealCreationDate?: Date | null
}

const RelatedDocumentsTable: FC<IDealDocumentProps> = (props) => {
  const classes = useTableComponentStyle()
  const { documents, clientId, dealCreationDate } = props
  const dealCreationYear = dealCreationDate ? dealCreationDate.getFullYear() : new Date().getFullYear()

  const s = useStyles()

  const handleDownLoadFile = useCallback((url, name) => {
    getFile(url).then((res) => {
      createLinkToDownLoad(res.data, name)
    })
  }, [])

  const { data: dataBankAccounts } = useQuery(['client-bank-accounts', clientId], () => getClientBankAccounts({ clientId: clientId ?? 0 }), {
    cacheTime: 0,
    retry: false,
    enabled: Boolean(clientId),
  })

  const allPrefixes = useMemo(() => {
    if (!dataBankAccounts?.data) return []
    const window = getRollingMonthWindow()
    return dataBankAccounts.data.flatMap(({ bankAccount }) => [
      ...window.map(({ month, year }) => getBankStatementFileName(bankAccount, month, year)),
      ...monthNames.map((month) => getBankStatementFileName(bankAccount, month, dealCreationYear)),
    ])
  }, [dataBankAccounts, dealCreationYear])

  const getDescription = useCallback((type: IUploadedCategoriesKeys) => {
    switch (type) {
      case 'Application':
        return 'Application'
      case 'DriverLicense':
        return "Driver's License"
      case 'VoidedCheck':
        return 'Voided check'
      case 'Contract':
        return 'Contract'
      case 'AchAuthorizationForm':
        return 'ACH Authorization Form'
      case 'BankStatements':
        return 'Bank Statements'
      case 'CreditProcessingStatements':
        return 'Credit Processing Statements'
      case 'W9':
        return 'W9'
      case 'Optional':
        return 'Optional'
      case 'FundingCallRecord':
        return 'Funding Call Record'
      case 'ExternalDealWireInstructions':
        return 'External Deal Wire Instructions'
      case 'ExternalDealPayoffLetter':
        return 'External Deal Payoff Letter'
      default:
        return type
    }
  }, [])

  const getType = useCallback(
    (doc: IDealDocumentConnectParamsResponse) => {
      if (doc.documentType === 'BankStatements') {
        const value = doc?.description || ''
        const isCategorized = allPrefixes.some((prefix) => value.startsWith(prefix))
        return isCategorized ? 'Bank Statements' : 'Bank Statements (optional)'
      }
      switch (doc.documentType) {
        case 'Application':
          return 'Application'
        case 'DriverLicense':
          return "Driver's License"
        case 'VoidedCheck':
          return 'Voided check'
        case 'Contract':
          return 'Contract'
        case 'AchAuthorizationForm':
          return 'ACH Authorization Form'
        case 'CreditProcessingStatements':
          return 'Credit Processing Statements'
        case 'W9':
          return 'W9'
        case 'Optional':
          return 'Optional'
        case 'ExternalDealWireInstructions':
          return 'External Deal Wire Instructions'
        case 'ExternalDealPayoffLetter':
          return 'External Deal Payoff Letter'
        default:
          return 'Optional'
      }
    },
    [allPrefixes]
  )

  // Filter state and logic
  const [selectedCategory, setSelectedCategory] = useState<string>('All')

  // Check if there are any optional bank statements in the documents
  const hasOptionalBankStatements = useMemo(() => {
    return documents.some((doc) => getType(doc) === 'Bank Statements (optional)')
  }, [documents, getType])

  const CATEGORY_OPTIONS = useMemo(() => {
    const baseOptions = [
      'All',
      'Application',
      "Driver's License",
      'Voided check',
      'Contract',
      'ACH Authorization Form',
      'Bank Statements',
      'W9',
      'Optional',
    ]

    // Add "Optional Bank Statements" only if there are documents of this type (for old deals)
    if (hasOptionalBankStatements) {
      const insertIndex = baseOptions.indexOf('W9')
      baseOptions.splice(insertIndex, 0, 'Bank Statements (optional)')
    }

    return baseOptions
  }, [hasOptionalBankStatements])

  const filteredDocuments = useMemo(() => {
    if (selectedCategory === 'All') {
      return documents
    }
    return documents.filter((doc) => getType(doc) === selectedCategory)
  }, [documents, selectedCategory, getType])

  const groupedDocuments = useMemo(() => {
    const groups: { [desc: string]: IDealDocumentConnectParamsResponse[] } = {}
    filteredDocuments.forEach((doc) => {
      const type = getType(doc)
      if (!groups[type]) groups[type] = []
      groups[type].push(doc)
    })
    return groups
  }, [filteredDocuments, getDescription, getType])

  const nameColumn: IColumn<IDealDocumentConnectParamsResponse> = {
    label: 'Name',
    field: 'name',
    bodyCellClassName: `${classes.accentCell} ${s.compactCell}`,
    render: (data) => (
      <Link
        to={data.documentUrl}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          handleDownLoadFile(data.documentUrl, data.description)
        }}
      >
        {data.description}
      </Link>
    ),
  }

  const dateColumn: IColumn<IDealDocumentConnectParamsResponse> = {
    label: 'Date & Time',
    field: 'createdDate',
    bodyCellClassName: `${classes.accentCell} ${s.compactCell} ${s.documentDateCell}`,
    render: (data) => (
      <Box mr={2}>
        <Typography variant="body2" color="textSecondary">
          {convertDateWithTime(data.createdDate)}
        </Typography>
      </Box>
    ),
  }

  const defaultColumns: IColumn<IDealDocumentConnectParamsResponse>[] = [nameColumn, dateColumn]

  const optionalColumns: IColumn<IDealDocumentConnectParamsResponse>[] = [
    nameColumn,
    {
      label: 'Document Type',
      field: 'documentType',
      bodyCellClassName: `${classes.accentCell} ${s.compactCell}`,
      render: (data) => getDescription(data.documentType) ?? '',
    },
    {
      label: 'Comment',
      field: 'comment',
      bodyCellClassName: `${classes.accentCell} ${s.compactCell}`,
      render: (data) => data.comment ?? '',
    },
    dateColumn,
  ]

  const DOCUMENT_ORDER = [
    'Application',
    "Driver's License",
    'Voided check',
    'Contract',
    'ACH Authorization Form',
    'Bank Statements',
    'Bank Statements (optional)',
    'Credit Processing Statements',
    'W9',
    'Optional',
  ]

  return (
    <div style={{ marginLeft: 46 }}>
      {/* Category Filter */}
      <div style={{ marginBottom: 20 }}>
        <SelectComponent
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value as string)}
          style={{ minWidth: 100, width: '50%' }}
        >
          {CATEGORY_OPTIONS.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </SelectComponent>
      </div>

      {Object.entries(groupedDocuments)
        .sort(([a], [b]) => DOCUMENT_ORDER.indexOf(a) - DOCUMENT_ORDER.indexOf(b))
        .map(([desc, docs]) => (
          <div key={desc}>
            <p
              style={{
                color: '#222',
                fontWeight: 700,
                fontSize: 16,
                margin: '0 0 0 0',
              }}
            >
              {desc}
            </p>
            <div className={s.groupBoxStyle}>
              <TableRelatedDocuments columns={desc === 'Optional' ? optionalColumns : defaultColumns} data={docs} hideHeader />
            </div>
          </div>
        ))}
    </div>
  )
}

export default RelatedDocumentsTable
